import { useQueries, UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CommandCenterPersonProfile, GUID, Keyword, PersonOrg } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { filterByDateRange } from 'utils';

import { PersonFilters, usePersonFiltersContext } from './PersonFiltersContext';

const combineKeywordPersonOrgGUIDs = (results: UseQueryResult<GUID<PersonOrg>[]>[]): Set<GUID<PersonOrg>> =>
    new Set(results.flatMap((result) => result.data ?? []));

const filterByNdvrEmploymentStatus = (person: CommandCenterPersonProfile, filters: PersonFilters) =>
    filters.ndvrEmploymentStatus.includes(person.personOrgShortProfile.ndvrEmploymentStatus!);

const filterByState = (person: CommandCenterPersonProfile, filters: PersonFilters) =>
    filters.states.includes(person.personOrgShortProfile.state!);

const filterByLastLoginAt = (person: CommandCenterPersonProfile, filters: PersonFilters): boolean => {
    return filterByDateRange(person.lastLoginAt!, filters.lastLoginAtStart, filters.lastLoginAtEnd);
};

export const usePersonFilters = (persons: CommandCenterPersonProfile[]) => {
    const { filters, isNdvrEmploymentStatusActive, isStatesFilterActive, isLastLoginAtActive, isKeywordsFilterActive } =
        usePersonFiltersContext();

    const personOrgGUIDsWithKeywords = useQueries({
        queries: filters.keywordGUIDs.map((keywordGUID) => ({
            queryKey: ['keyword', keywordGUID, 'personOrgs'],
            queryFn: async () => {
                const [response] = await CustomerApi().getPersonOrgsByKeyword(keywordGUID);
                return response ?? [];
            },
            enabled: isKeywordsFilterActive,
        })),
        combine: combineKeywordPersonOrgGUIDs,
    });

    const activeFilters = useMemo(
        () =>
            [
                {
                    isActive: isNdvrEmploymentStatusActive,
                    filterFunction: (person: CommandCenterPersonProfile) =>
                        filterByNdvrEmploymentStatus(person, filters),
                },
                {
                    isActive: isStatesFilterActive,
                    filterFunction: (person: CommandCenterPersonProfile) => filterByState(person, filters),
                },
                {
                    isActive: isLastLoginAtActive,
                    filterFunction: (person: CommandCenterPersonProfile) => filterByLastLoginAt(person, filters),
                },
                {
                    isActive: isKeywordsFilterActive,
                    filterFunction: (person: CommandCenterPersonProfile) =>
                        personOrgGUIDsWithKeywords.has(person.personOrgShortProfile.personOrgGUID),
                },
            ].filter(({ isActive }) => isActive),
        [
            filters,
            isNdvrEmploymentStatusActive,
            isStatesFilterActive,
            isLastLoginAtActive,
            isKeywordsFilterActive,
            personOrgGUIDsWithKeywords,
        ],
    );

    const filteredPersons = useMemo(
        () => persons.filter((person) => activeFilters.every(({ filterFunction }) => filterFunction(person))),
        [persons, activeFilters],
    );

    return {
        data: filteredPersons,
    };
};
